import { useState, useEffect } from 'react';
import type { DeviceSwitchHistory } from '../types';
import { api } from '../utils/api';

interface DeviceSwitchHistoryModalProps {
  isOpen: boolean;
  deviceId?: string;
  employeeId?: string;
  onClose: () => void;
}

export default function DeviceSwitchHistoryModal({
  isOpen,
  deviceId,
  employeeId,
  onClose
}: DeviceSwitchHistoryModalProps) {
  const [history, setHistory] = useState<DeviceSwitchHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;

    const fetchHistory = async () => {
      setLoading(true);
      setError('');
      try {
        let data: DeviceSwitchHistory[] = [];

        if (deviceId) {
          data = await api.getDeviceSwitchHistoryByDeviceId(deviceId, 50);
        } else if (employeeId) {
          data = await api.getDeviceSwitchHistoryByEmployeeId(employeeId, 50);
        }

        setHistory(data);
      } catch (err) {
        console.error('Failed to fetch history:', err);
        setError('Gagal mengambil riwayat perubahan device');
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [isOpen, deviceId, employeeId]);

  const getActionLabel = (actionType: string) => {
    const labels: Record<string, string> = {
      assign: 'Ditugaskan',
      unassign: 'Dibatalkan',
      switch: 'Ditukar',
      new_device: 'New Devices'
    };
    return labels[actionType] || actionType;
  };

  const getActionColor = (actionType: string) => {
    const colors: Record<string, string> = {
      assign: 'bg-green-100 text-green-800',
      unassign: 'bg-red-100 text-red-800',
      switch: 'bg-blue-100 text-blue-800',
      new_device: 'bg-purple-100 text-purple-800'
    };
    return colors[actionType] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full mx-4 max-h-full overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
          <h2 className="text-lg font-semibold">Riwayat Perubahan Device</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="p-4">
          {loading && (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <p className="mt-2 text-gray-600">Memuat riwayat...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {!loading && !error && history.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              Tidak ada riwayat perubahan device
            </div>
          )}

          {!loading && !error && history.length > 0 && (
            <div className="space-y-3">
              {history.map((record) => (
                <div
                  key={record.id}
                  className="shadow rounded-lg p-3 hover:bg-gray-50 transition"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getActionColor(record.action_type)}`}>
                          {getActionLabel(record.action_type)}
                        </span>
                        <span className="text-sm text-gray-600">
                          {formatDate(record.created_at)}
                        </span>
                      </div>
                      <div className="text-sm text-gray-700">
                        <p>
                          <strong>Device:</strong> {record.device?.name || 'N/A'}
                        </p>
                        {record.action_type === 'unassign' ? (
                          <>
                            <p>
                              <strong>Status:</strong> Unassigned (Tidak ada employee)
                            </p>
                            <p>
                              <strong>Karyawan Sebelumnya:</strong> {record.old_employee?.Nama || 'N/A'} ({record.old_employee?.NIP || 'N/A'})
                            </p>
                          </>
                        ) : (
                          <>
                            <p>
                              <strong>Karyawan Saat Ini:</strong> {record.employee?.Nama || 'N/A'} ({record.employee?.NIP || 'N/A'})
                            </p>
                            {record.old_employee && (
                              <p>
                                <strong>Karyawan Sebelumnya:</strong> {record.old_employee.Nama} ({record.old_employee.NIP || 'N/A'})
                              </p>
                            )}
                          </>
                        )}
                        {record.notes && (
                          <p className="text-gray-600 mt-1">
                            <strong>Catatan:</strong> {record.notes}
                          </p>
                        )}
                        {record.created_by_user && (
                          <p className="text-xs text-gray-500 mt-1">
                            <strong>Oleh:</strong> {record.created_by_user.nama} ({record.created_by_user.email})
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-gray-200  p-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition"
          >
            Tutup
          </button>
        </div>
      </div>
    </div>
  );
}
